import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useRef } from 'react';
import { ForestTodayButton, forestArrow, forestGroup } from '@/shared/components/ForestTools';
import { cn } from '@/shared/lib/cn';
import { addDaysIso } from './calendar/cleaningCalendarHelpers';

/** „Heute · Di., 20. Oktober“ — Datum roh als Wanduhr, ohne Verschiebung durch die Gerätezeit. */
export function dayTitle(date: string, today: string): string {
  const label = new Intl.DateTimeFormat('de-DE', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
  if (date === today) return `Heute · ${label}`;
  if (date === addDaysIso(today, 1)) return `Morgen · ${label}`;
  if (date === addDaysIso(today, -1)) return `Gestern · ${label}`;
  return label;
}

/**
 * Datum vor und zurück auf dem Wald-Kopf (Tag oder Woche); das Datum selbst öffnet die
 * Datumswahl, damit die Leitung Wochen voraus nicht Tag für Tag tippen muss. „Heute“ erscheint,
 * sobald der heutige Tag nicht im gewählten Zeitraum liegt.
 */
export function CleaningDateNav({
  label,
  unit,
  date,
  showToday,
  onShift,
  onPick,
  onToday,
}: {
  label: string;
  unit: 'day' | 'week';
  date: string;
  showToday: boolean;
  onShift: (direction: -1 | 1) => void;
  onPick: (date: string) => void;
  onToday: () => void;
}) {
  const picker = useRef<HTMLInputElement>(null);
  const noun = unit === 'day' ? 'Tag' : 'Woche';
  const open = () => {
    const input = picker.current;
    if (!input) return;
    try {
      input.showPicker();
    } catch {
      input.focus();
    }
  };
  return (
    <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
      <div className={cn(forestGroup, 'relative min-w-0 flex-1 sm:flex-none')}>
        <button
          type="button"
          aria-label={unit === 'day' ? 'Vorheriger Tag' : 'Vorherige Woche'}
          className={forestArrow}
          onClick={() => onShift(-1)}
        >
          <ChevronLeft size={18} aria-hidden />
        </button>
        <button
          type="button"
          aria-label={`${noun}: ${label} – Datum wählen`}
          onClick={open}
          className="rounded-pill min-h-11 min-w-0 flex-1 truncate px-1 text-center text-[15px] font-bold hover:bg-white/10 sm:min-w-44"
        >
          <span aria-live="polite">{label}</span>
        </button>
        <input
          ref={picker}
          type="date"
          tabIndex={-1}
          aria-hidden
          value={date}
          onChange={(event) => event.target.value && onPick(event.target.value)}
          className="pointer-events-none absolute bottom-0 left-1/2 h-0 w-0 opacity-0"
        />
        <button
          type="button"
          aria-label={unit === 'day' ? 'Nächster Tag' : 'Nächste Woche'}
          className={forestArrow}
          onClick={() => onShift(1)}
        >
          <ChevronRight size={18} aria-hidden />
        </button>
      </div>
      {showToday && <ForestTodayButton onToday={onToday} />}
    </div>
  );
}
